import { useMutation, useQuery } from 'react-query';
import RestHttpClient from '../../../common/RestHttpClient';
import {
  SendPurchaseApplicationRequest,
  SignPurchaseApplicationRequest,
} from '../../../generated/ApiClient';
import { EnhancedDialog } from '../../../component/EnhancedDialog';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Print } from '@mui/icons-material';
import PDFPreviewDialog, { PdfPreview } from '../../../component/PDFPreviewDialog';
import { ProgressStatusLoading } from '../../../component/progress-status/ProgressStatusLoading';
import { ProgressStatusFinished } from '../../../component/progress-status/ProgressStatusFinished';
import { SignatureQrCode } from '../../../component/SignatureQrCode';

interface Props {
  contractNumber: string;
  dealId: number;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  fileId?: string;
  isApplicationLoading: boolean;
}

export function PurchaseApplicationDialog({
  dealId,
  isOpen,
  setIsOpen,
  fileId,
  isApplicationLoading,
}: Props) {
  const { t } = useTranslation();
  const [openDocFile, setOpenDocFile] = useState<PdfPreview | undefined>();

  const {
    data: signDocument,
    refetch: signPurchaseDocument,
    isLoading: isSigning,
  } = useQuery({
    queryKey: ['purchase-application-sign', dealId, fileId],
    queryFn: () =>
      RestHttpClient.signPurchaseApplication(
        new SignPurchaseApplicationRequest({ fileId: fileId!, dealId: dealId })
      ),
    enabled: false,
  });

  const onPrint = () => {
    if (fileId) {
      RestHttpClient.getUploadedDocument(fileId).then((res) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(res.data);
        link.download = res.fileName ?? 'download';
        link.click();
      });
    }
  };

  return (
    <>
      <EnhancedDialog isDialogOpened={isOpen} setIsDialogOpened={setIsOpen}>
        <DialogTitle>
          {t('dashboard.contractsOverlay.dissolveValues.purchaseApplication.title')}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} p={3}>
            {isApplicationLoading && (
              <ProgressStatusLoading
                label={t('dashboard.contractsOverlay.dissolveValues.purchaseApplication.loading')}
              />
            )}
            {fileId && (
              <ProgressStatusFinished
                label={t('dashboard.contractsOverlay.dissolveValues.purchaseApplication.ready')}
              />
            )}
            {isSigning ? (
              <ProgressStatusLoading
                label={t(
                  'dashboard.contractsOverlay.dissolveValues.purchaseApplication.signatureCheck'
                )}
              />
            ) : signDocument?.data ? (
              <Stack gap={4}>
                <ProgressStatusFinished
                  label={t(
                    'dashboard.contractsOverlay.dissolveValues.purchaseApplication.signatureReady'
                  )}
                />
                <SignatureQrCode signDocument={signDocument.data} />
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack flexDirection="row" justifyContent="flex-end" gap={2}>
            <Button variant="contained" disabled={!fileId} onClick={onPrint} endIcon={<Print />}>
              {t('dashboard.contractsOverlay.dissolveValues.purchaseApplication.print')}
            </Button>
            <Button
              variant="contained"
              disabled={!fileId || !!signDocument}
              onClick={() => signPurchaseDocument()}
              endIcon={<Print />}
            >
              {t('dashboard.contractsOverlay.dissolveValues.purchaseApplication.sign')}
            </Button>
          </Stack>
        </DialogActions>
      </EnhancedDialog>
      <PDFPreviewDialog file={openDocFile} onClose={() => setOpenDocFile(undefined)} />
    </>
  );
}
